import type { WriteTransaction } from "replicache";
import {
  attachmentKey,
  canvasKey,
  folderKey,
  groupKey,
  itemKey,
  noteKey,
  sceneKey,
  timeEntryKey,
  type Attachment,
  type AttachmentStatus,
  type Canvas,
  type Folder,
  type Group,
  type Item,
  type ItemPriority,
  type ItemStatus,
  type Note,
  type Scene,
  type TimeEntry,
  type ItemRecurrence,
} from "./keys";

// Spec §6.1: client and server mutators must agree.  The client runs these
// optimistically against the local Replicache cache; the server runs them
// authoritatively against Postgres and the pull patch overwrites any drift.

export type CreateGroupArgs = {
  id: string;
  name: string;
  color?: string;
  icon?: string | null;
  sortKey: string;
  folderId?: string | null;
};
export type UpdateGroupArgs = Partial<CreateGroupArgs> & { id: string };

export type CreateFolderArgs = {
  id: string;
  name: string;
  sortKey: string;
};
export type UpdateFolderArgs = Partial<CreateFolderArgs> & { id: string };

export type SetSharedGroupViewArgs = {
  groupId: string;
  sortKey?: string;
  folderId?: string | null;
};

export type CreateNoteArgs = {
  id: string;
  name?: string;
  color?: string;
  icon?: string | null;
  body?: string;
  sortKey: string;
  folderId?: string | null;
};
export type UpdateNoteArgs = Partial<Omit<CreateNoteArgs, "id">> & { id: string };

export type CreateCanvasArgs = {
  id: string;
  name?: string;
  strokes?: string;
  sortKey: string;
};
export type UpdateCanvasArgs = Partial<Omit<CreateCanvasArgs, "id">> & { id: string };

const SCENE_DEFAULT = '{"elements":[],"appState":{}}';

export type CreateSceneArgs = {
  id: string;
  name?: string;
  scene?: string;
  sortKey: string;
};
export type UpdateSceneArgs = Partial<Omit<CreateSceneArgs, "id">> & { id: string };

export type CreateItemArgs = {
  id: string;
  groupId: string;
  parentId: string | null;
  type: "task" | "header";
  title?: string;
  notes?: string;
  status?: ItemStatus;
  priority?: ItemPriority | null;
  isFocused?: boolean;
  startDate?: string | null;
  dueDate?: string | null;
  recurrence?: ItemRecurrence | null;
  reportingTo?: string | null;
  sortKey: string;
};
export type UpdateItemArgs = {
  id: string;
  title?: string;
  notes?: string;
  status?: ItemStatus;
  priority?: ItemPriority | null;
  isFocused?: boolean;
  startDate?: string | null;
  dueDate?: string | null;
  recurrence?: ItemRecurrence | null;
  reportingTo?: string | null;
};
export type MoveItemArgs = {
  id: string;
  groupId?: string;
  parentId?: string | null;
  sortKey: string;
};

export type AddTimeEntryArgs = {
  id: string;
  itemId: string;
  startedAt: string;
  endedAt: string;
  note?: string;
};
export type UpdateTimeEntryArgs = {
  id: string;
  startedAt?: string;
  endedAt?: string;
  note?: string;
};

// Same computation the server's STORED column performs.  Kept in lock-step so
// optimistic durations agree with what pull will return.
function computeDurationMinutes(startedAt: string, endedAt: string): number {
  const ms = new Date(endedAt).getTime() - new Date(startedAt).getTime();
  return Math.max(0, Math.round(ms / 60_000));
}

export type CreateAttachmentArgs = {
  id: string;
  itemId: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  storageKey: string;
};
export type MarkAttachmentUploadedArgs = {
  id: string;
  status: "uploaded" | "failed";
};

// Mutators are constructed per Replicache instance, closing over the
// current userId so optimistic writes can stamp the right ownerId without
// every call site threading it through.  The server's authoritative
// mutator may set a different owner (e.g. a recipient creating an item in
// a shared scope ends up as the share owner's item) — the next pull
// corrects the local row if so.

export function makeMutators(currentUserId: string) {
  return {
  async createGroup(tx: WriteTransaction, args: CreateGroupArgs) {
    const now = new Date().toISOString();
    const value: Group = {
      id: args.id,
      ownerId: currentUserId,
      name: args.name,
      color: args.color ?? "slate",
      icon: args.icon ?? null,
      sortKey: args.sortKey,
      folderId: args.folderId ?? null,
      createdAt: now,
      updatedAt: now,
      // 0 = optimistic, not yet server-stamped.  The next pull replaces
      // this with the real monotonic version; the sync-integrity check
      // is gated on pendingCount === 0, so a temporarily-stale version
      // here can't trip a false drift detection.
      version: 0,
    };
    await tx.set(groupKey(args.id), value);
  },

  async updateGroup(tx: WriteTransaction, args: UpdateGroupArgs) {
    const existing = (await tx.get(groupKey(args.id))) as Group | undefined;
    if (!existing) return;
    const next: Group = {
      ...existing,
      ...(args.name !== undefined ? { name: args.name } : {}),
      ...(args.color !== undefined ? { color: args.color } : {}),
      ...(args.icon !== undefined ? { icon: args.icon } : {}),
      ...(args.sortKey !== undefined ? { sortKey: args.sortKey } : {}),
      ...(args.folderId !== undefined ? { folderId: args.folderId } : {}),
      updatedAt: new Date().toISOString(),
    };
    await tx.set(groupKey(args.id), next);
  },

  async createFolder(tx: WriteTransaction, args: CreateFolderArgs) {
    const now = new Date().toISOString();
    const value: Folder = {
      id: args.id,
      name: args.name,
      sortKey: args.sortKey,
      createdAt: now,
      updatedAt: now,
      version: 0,
    };
    await tx.set(folderKey(args.id), value);
  },

  async updateFolder(tx: WriteTransaction, args: UpdateFolderArgs) {
    const existing = (await tx.get(folderKey(args.id))) as Folder | undefined;
    if (!existing) return;
    const next: Folder = {
      ...existing,
      ...(args.name !== undefined ? { name: args.name } : {}),
      ...(args.sortKey !== undefined ? { sortKey: args.sortKey } : {}),
      updatedAt: new Date().toISOString(),
    };
    await tx.set(folderKey(args.id), next);
  },

  async createNote(tx: WriteTransaction, args: CreateNoteArgs) {
    const now = new Date().toISOString();
    const value: Note = {
      id: args.id,
      ownerId: currentUserId,
      name: args.name ?? "",
      color: args.color ?? "#94a3b8",
      icon: args.icon ?? null,
      body: args.body ?? "",
      sortKey: args.sortKey,
      folderId: args.folderId ?? null,
      createdAt: now,
      updatedAt: now,
      version: 0,
    };
    await tx.set(noteKey(args.id), value);
  },

  async updateNote(tx: WriteTransaction, args: UpdateNoteArgs) {
    const existing = (await tx.get(noteKey(args.id))) as Note | undefined;
    if (!existing) return;
    const next: Note = {
      ...existing,
      ...(args.name !== undefined ? { name: args.name } : {}),
      ...(args.color !== undefined ? { color: args.color } : {}),
      ...(args.icon !== undefined ? { icon: args.icon } : {}),
      ...(args.body !== undefined ? { body: args.body } : {}),
      ...(args.sortKey !== undefined ? { sortKey: args.sortKey } : {}),
      ...(args.folderId !== undefined ? { folderId: args.folderId } : {}),
      updatedAt: new Date().toISOString(),
    };
    await tx.set(noteKey(args.id), next);
  },

  async deleteNote(tx: WriteTransaction, args: { id: string }) {
    await tx.del(noteKey(args.id));
  },

  async createCanvas(tx: WriteTransaction, args: CreateCanvasArgs) {
    const now = new Date().toISOString();
    const value: Canvas = {
      id: args.id,
      ownerId: currentUserId,
      name: args.name ?? "",
      strokes: args.strokes ?? "[]",
      sortKey: args.sortKey,
      createdAt: now,
      updatedAt: now,
      version: 0,
    };
    await tx.set(canvasKey(args.id), value);
  },

  async updateCanvas(tx: WriteTransaction, args: UpdateCanvasArgs) {
    const existing = (await tx.get(canvasKey(args.id))) as Canvas | undefined;
    if (!existing) return;
    const next: Canvas = {
      ...existing,
      ...(args.name !== undefined ? { name: args.name } : {}),
      ...(args.strokes !== undefined ? { strokes: args.strokes } : {}),
      ...(args.sortKey !== undefined ? { sortKey: args.sortKey } : {}),
      updatedAt: new Date().toISOString(),
    };
    await tx.set(canvasKey(args.id), next);
  },

  async deleteCanvas(tx: WriteTransaction, args: { id: string }) {
    await tx.del(canvasKey(args.id));
  },

  async createScene(tx: WriteTransaction, args: CreateSceneArgs) {
    const now = new Date().toISOString();
    const value: Scene = {
      id: args.id,
      ownerId: currentUserId,
      name: args.name ?? "",
      scene: args.scene ?? SCENE_DEFAULT,
      sortKey: args.sortKey,
      createdAt: now,
      updatedAt: now,
      version: 0,
    };
    await tx.set(sceneKey(args.id), value);
  },

  async updateScene(tx: WriteTransaction, args: UpdateSceneArgs) {
    const existing = (await tx.get(sceneKey(args.id))) as Scene | undefined;
    if (!existing) return;
    const next: Scene = {
      ...existing,
      ...(args.name !== undefined ? { name: args.name } : {}),
      ...(args.scene !== undefined ? { scene: args.scene } : {}),
      ...(args.sortKey !== undefined ? { sortKey: args.sortKey } : {}),
      updatedAt: new Date().toISOString(),
    };
    await tx.set(sceneKey(args.id), next);
  },

  async deleteScene(tx: WriteTransaction, args: { id: string }) {
    await tx.del(sceneKey(args.id));
  },

  async deleteFolder(tx: WriteTransaction, args: { id: string }) {
    await tx.del(folderKey(args.id));
    // Un-file any groups optimistically so the sidebar updates instantly.
    // The server's deleteFolder mutator does the same (bumps each group's
    // version), so the next pull's "del+put" pair for the same group is a
    // no-op against this optimistic state.  Covers both owner-side groups
    // (folder_id on groups) and shared-in groups (folder_id on
    // group_view_prefs) — the optimistic local cache has both materialised
    // onto the Group row.
    for await (const [key, value] of tx.scan({ prefix: groupKey("") }).entries()) {
      const g = value as Group;
      if (g.folderId === args.id) {
        await tx.set(key, { ...g, folderId: null });
      }
    }
    for await (const [key, value] of tx.scan({ prefix: noteKey("") }).entries()) {
      const n = value as Note;
      if (n.folderId === args.id) {
        await tx.set(key, { ...n, folderId: null });
      }
    }
  },

  // Recipient-side override of a shared group's sortKey/folderId.
  // Optimistic write to the local Group row; the next pull will rebase
  // from the prefs row on the server.
  async setSharedGroupView(tx: WriteTransaction, args: SetSharedGroupViewArgs) {
    const existing = (await tx.get(groupKey(args.groupId))) as
      | Group
      | undefined;
    if (!existing) return;
    const next: Group = {
      ...existing,
      ...(args.sortKey !== undefined ? { sortKey: args.sortKey } : {}),
      ...(args.folderId !== undefined ? { folderId: args.folderId } : {}),
      updatedAt: new Date().toISOString(),
    };
    await tx.set(groupKey(args.groupId), next);
  },

  async deleteGroup(tx: WriteTransaction, args: { id: string }) {
    await tx.del(groupKey(args.id));
    // Cascade delete items + their time entries locally — server does the
    // same, so subsequent pulls agree.  Collect item ids first so we can
    // match time entries by item_id.
    const deletedItemIds = new Set<string>();
    for await (const [key, value] of tx.scan({ prefix: itemKey("") }).entries()) {
      const item = value as Item;
      if (item.groupId === args.id) {
        deletedItemIds.add(item.id);
        await tx.del(key);
      }
    }
    for await (const [key, value] of tx.scan({ prefix: timeEntryKey("") }).entries()) {
      const entry = value as TimeEntry;
      if (deletedItemIds.has(entry.itemId)) await tx.del(key);
    }
    for await (const [key, value] of tx.scan({ prefix: attachmentKey("") }).entries()) {
      const att = value as Attachment;
      if (deletedItemIds.has(att.itemId)) await tx.del(key);
    }
  },

  async createItem(tx: WriteTransaction, args: CreateItemArgs) {
    const now = new Date().toISOString();
    const status = args.status ?? "not_started";
    // Optimistic ownerId = me.  Server may rewrite to a share owner if
    // we're creating under a parent we have edit access to but don't own;
    // pull will correct the row.
    const value: Item = {
      id: args.id,
      ownerId: currentUserId,
      groupId: args.groupId,
      parentId: args.parentId,
      type: args.type,
      title: args.title ?? "",
      notes: args.notes ?? "",
      status,
      priority: args.priority ?? null,
      isFocused: args.isFocused ?? false,
      startDate: args.startDate ?? null,
      dueDate: args.dueDate ?? null,
      recurrence: args.recurrence ?? null,
      reportingTo: args.reportingTo ?? null,
      sortKey: args.sortKey,
      isComplete: status === "done",
      createdAt: now,
      updatedAt: now,
      version: 0,
    };
    await tx.set(itemKey(args.id), value);
  },

  async updateItem(tx: WriteTransaction, args: UpdateItemArgs) {
    const existing = (await tx.get(itemKey(args.id))) as Item | undefined;
    if (!existing) return;
    const status = args.status ?? existing.status;
    const next: Item = {
      ...existing,
      ...(args.title !== undefined ? { title: args.title } : {}),
      ...(args.notes !== undefined ? { notes: args.notes } : {}),
      ...(args.status !== undefined ? { status } : {}),
      isComplete: status === "done",
      ...(args.priority !== undefined ? { priority: args.priority } : {}),
      ...(args.isFocused !== undefined ? { isFocused: args.isFocused } : {}),
      ...(args.startDate !== undefined ? { startDate: args.startDate } : {}),
      ...(args.dueDate !== undefined ? { dueDate: args.dueDate } : {}),
      ...(args.recurrence !== undefined ? { recurrence: args.recurrence } : {}),
      ...(args.reportingTo !== undefined ? { reportingTo: args.reportingTo } : {}),
      updatedAt: new Date().toISOString(),
    };
    await tx.set(itemKey(args.id), next);
  },

  async moveItem(tx: WriteTransaction, args: MoveItemArgs) {
    const existing = (await tx.get(itemKey(args.id))) as Item | undefined;
    if (!existing) return;
    const next: Item = {
      ...existing,
      ...(args.groupId !== undefined ? { groupId: args.groupId } : {}),
      ...(args.parentId !== undefined ? { parentId: args.parentId } : {}),
      sortKey: args.sortKey,
      updatedAt: new Date().toISOString(),
    };
    await tx.set(itemKey(args.id), next);
  },

  async deleteItem(tx: WriteTransaction, args: { id: string }) {
    // Collect every descendant id (incl. the root) before deleting, so we can
    // sweep their time entries afterwards.
    const deleted = new Set<string>([args.id]);
    await tx.del(itemKey(args.id));
    const queue = [args.id];
    while (queue.length) {
      const parent = queue.shift()!;
      for await (const [key, value] of tx.scan({ prefix: itemKey("") }).entries()) {
        const item = value as Item;
        if (item.parentId === parent) {
          await tx.del(key);
          deleted.add(item.id);
          queue.push(item.id);
        }
      }
    }
    for await (const [key, value] of tx.scan({ prefix: timeEntryKey("") }).entries()) {
      const entry = value as TimeEntry;
      if (deleted.has(entry.itemId)) await tx.del(key);
    }
    for await (const [key, value] of tx.scan({ prefix: attachmentKey("") }).entries()) {
      const att = value as Attachment;
      if (deleted.has(att.itemId)) await tx.del(key);
    }
  },

  async completeItem(tx: WriteTransaction, args: { id: string }) {
    const existing = (await tx.get(itemKey(args.id))) as Item | undefined;
    if (!existing) return;
    if (existing.isComplete) return;
    // Recurring tasks reset instead of completing — dates advance to the
    // next occurrence and the task stays open.  MUST stay in lockstep
    // with the server logic in api/src/replicache/mutators.ts
    // (setItemComplete + nextOccurrence).
    if (existing.recurrence && existing.dueDate) {
      const nextDue = nextOccurrence(existing.dueDate, existing.recurrence);
      const spanDays = existing.startDate
        ? daysBetweenISO(existing.startDate, existing.dueDate)
        : null;
      await tx.set(itemKey(args.id), {
        ...existing,
        status: "not_started",
        isComplete: false,
        startDate: spanDays === null ? null : addDaysISO(nextDue, -spanDays),
        dueDate: nextDue,
        updatedAt: new Date().toISOString(),
      });
      return;
    }
    await tx.set(itemKey(args.id), {
      ...existing,
      status: "done",
      isComplete: true,
      updatedAt: new Date().toISOString(),
    });
  },

  async uncompleteItem(tx: WriteTransaction, args: { id: string }) {
    const existing = (await tx.get(itemKey(args.id))) as Item | undefined;
    if (!existing) return;
    if (!existing.isComplete) return;
    await tx.set(itemKey(args.id), {
      ...existing,
      status: "not_started",
      isComplete: false,
      updatedAt: new Date().toISOString(),
    });
  },

  async addTimeEntry(tx: WriteTransaction, args: AddTimeEntryArgs) {
    // Skip if the target item doesn't exist locally — mirrors the server's
    // INSERT…SELECT-FROM-items guard.
    const item = (await tx.get(itemKey(args.itemId))) as Item | undefined;
    if (!item) return;
    const now = new Date().toISOString();
    const value: TimeEntry = {
      id: args.id,
      itemId: args.itemId,
      startedAt: args.startedAt,
      endedAt: args.endedAt,
      durationMinutes: computeDurationMinutes(args.startedAt, args.endedAt),
      note: args.note ?? "",
      createdAt: now,
      updatedAt: now,
      version: 0,
    };
    await tx.set(timeEntryKey(args.id), value);
  },

  async updateTimeEntry(tx: WriteTransaction, args: UpdateTimeEntryArgs) {
    const existing = (await tx.get(timeEntryKey(args.id))) as
      | TimeEntry
      | undefined;
    if (!existing) return;
    const startedAt = args.startedAt ?? existing.startedAt;
    const endedAt = args.endedAt ?? existing.endedAt;
    const next: TimeEntry = {
      ...existing,
      startedAt,
      endedAt,
      durationMinutes: computeDurationMinutes(startedAt, endedAt),
      ...(args.note !== undefined ? { note: args.note } : {}),
      updatedAt: new Date().toISOString(),
    };
    await tx.set(timeEntryKey(args.id), next);
  },

  async deleteTimeEntry(tx: WriteTransaction, args: { id: string }) {
    await tx.del(timeEntryKey(args.id));
  },

  async createAttachment(tx: WriteTransaction, args: CreateAttachmentArgs) {
    const item = (await tx.get(itemKey(args.itemId))) as Item | undefined;
    if (!item) return;
    const now = new Date().toISOString();
    const value: Attachment = {
      id: args.id,
      itemId: args.itemId,
      filename: args.filename,
      mimeType: args.mimeType,
      sizeBytes: args.sizeBytes,
      storageKey: args.storageKey,
      uploadStatus: "pending",
      createdAt: now,
      updatedAt: now,
      version: 0,
    };
    await tx.set(attachmentKey(args.id), value);
  },

  async markAttachmentUploaded(
    tx: WriteTransaction,
    args: MarkAttachmentUploadedArgs,
  ) {
    const existing = (await tx.get(attachmentKey(args.id))) as
      | Attachment
      | undefined;
    if (!existing) return;
    const next: Attachment = {
      ...existing,
      uploadStatus: args.status satisfies AttachmentStatus,
      updatedAt: new Date().toISOString(),
    };
    await tx.set(attachmentKey(args.id), next);
  },

  async deleteAttachment(tx: WriteTransaction, args: { id: string }) {
    await tx.del(attachmentKey(args.id));
  },

  // Archive: locally, identical to deleteItem — the subtree must vanish
  // from the live IDB cache so it stops appearing in tree/Gantt views.
  // Server-side, the archive mutator stamps archived_at instead of
  // deleted_at; pull then filters it out, matching what we did here
  // optimistically.  Restore lives on the /archive page (server-only —
  // we don't have the archived rows locally to replay snapshot-style).
  async archiveItem(tx: WriteTransaction, args: { id: string }) {
    const archived = new Set<string>([args.id]);
    await tx.del(itemKey(args.id));
    const queue = [args.id];
    while (queue.length) {
      const parent = queue.shift()!;
      for await (const [key, value] of tx.scan({ prefix: itemKey("") }).entries()) {
        const item = value as Item;
        if (item.parentId === parent) {
          await tx.del(key);
          archived.add(item.id);
          queue.push(item.id);
        }
      }
    }
    for await (const [key, value] of tx.scan({ prefix: timeEntryKey("") }).entries()) {
      const entry = value as TimeEntry;
      if (archived.has(entry.itemId)) await tx.del(key);
    }
    for await (const [key, value] of tx.scan({ prefix: attachmentKey("") }).entries()) {
      const att = value as Attachment;
      if (archived.has(att.itemId)) await tx.del(key);
    }
  },

  // Unarchive: server-only effect.  We don't hold archived rows in IDB
  // so there's nothing to optimistically restore — the next pull will
  // re-insert the subtree once the server has cleared archived_at.
  async unarchiveItem(_tx: WriteTransaction, _args: { id: string }) {
    // intentional no-op locally.
  },

  // Inverse of deleteItem: re-insert the snapshot captured by the undo
  // stack so the UI shows the item again instantly.  Server-side, this
  // mutator just clears `deleted_at` on the root + descendants in the DB —
  // the args.id is what it keys on.  The snapshots in args are
  // client-side optimistic content; the server schema (DeleteIdArgs) only
  // requires id, so it harmlessly ignores the rest.
  async restoreItem(
    tx: WriteTransaction,
    args: {
      id: string;
      item: Item;
      descendants?: Item[];
      timeEntries?: TimeEntry[];
      attachments?: Attachment[];
    },
  ) {
    const now = new Date().toISOString();
    await tx.set(itemKey(args.item.id), { ...args.item, updatedAt: now });
    for (const d of args.descendants ?? []) {
      await tx.set(itemKey(d.id), { ...d, updatedAt: now });
    }
    for (const t of args.timeEntries ?? []) {
      await tx.set(timeEntryKey(t.id), t);
    }
    for (const a of args.attachments ?? []) {
      await tx.set(attachmentKey(a.id), a);
    }
  },
  };
}

export type AppMutators = ReturnType<typeof makeMutators>;

// --- Recurrence date math ----------------------------------------------------
// Duplicated verbatim from api/src/replicache/mutators.ts — the optimistic
// client result must match the authoritative server result or every
// recurring completion causes a visible rebase.

function addDaysISO(iso: string, days: number): string {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  const dt = new Date(Date.UTC(y!, (m ?? 1) - 1, (d ?? 1) + days));
  return dt.toISOString().slice(0, 10);
}

function daysBetweenISO(a: string, b: string): number {
  const ms =
    Date.parse(b.slice(0, 10) + "T00:00:00Z") -
    Date.parse(a.slice(0, 10) + "T00:00:00Z");
  return Math.round(ms / 86_400_000);
}

function addMonthISO(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  // Clamp to the target month's last day (Jan 31 -> Feb 28/29).
  const daysInTarget = new Date(Date.UTC(y!, m! + 1, 0)).getUTCDate();
  const dt = new Date(Date.UTC(y!, m!, Math.min(d!, daysInTarget)));
  return dt.toISOString().slice(0, 10);
}

export function nextOccurrence(
  dueISO: string,
  rule: ItemRecurrence,
): string {
  const todayUTC = new Date().toISOString().slice(0, 10);
  let due = dueISO.slice(0, 10);
  do {
    due =
      rule === "daily"
        ? addDaysISO(due, 1)
        : rule === "weekly"
          ? addDaysISO(due, 7)
          : addMonthISO(due);
  } while (due <= todayUTC);
  return due;
}
